import { useState, useRef, useEffect } from 'react';
import { 
  Dialog, 
  IconButton, 
  Box, 
  Typography, 
  AppBar, 
  Toolbar,
  Button,
  CircularProgress,
  Badge
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import FlipCameraIosIcon from '@mui/icons-material/FlipCameraIos';
import DeleteIcon from '@mui/icons-material/Delete';

interface CameraCaptureProps {
  open: boolean;
  onClose: () => void;
  onCapture: (files: File[]) => Promise<boolean>;
  isUploading: boolean;
}

const CameraCapture = ({ open, onClose, onCapture, isUploading }: CameraCaptureProps) => {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [capturedImages, setCapturedImages] = useState<string[]>([]);
  const [isCapturing, setIsCapturing] = useState(false);
  const [flashEffect, setFlashEffect] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const thumbnailsRef = useRef<HTMLDivElement>(null);

  // Initialize camera when dialog opens
  useEffect(() => {
    if (open) {
      startCamera();
    } else {
      stopCamera();
      setCapturedImages([]);
    }
  }, [open, facingMode]);

  const startCamera = async () => {
    try {
      if (stream) {
        stopCamera();
      }
      
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: false
      });
      
      setStream(mediaStream);
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  };

  const switchCamera = () => {
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
  };

  const takePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    setIsCapturing(true);
    
    // Add flash effect
    setFlashEffect(true);
    setTimeout(() => setFlashEffect(false), 200);
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    if (!context) return;
    
    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Draw the video frame to the canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    // Convert to image data URL (JPEG format)
    const imageDataUrl = canvas.toDataURL('image/jpeg', 0.9);
    
    // Add to captured images
    setCapturedImages(prev => [...prev, imageDataUrl]);
    setIsCapturing(false);
    
    // Scroll to the end of thumbnails container to show the newest image
    setTimeout(() => {
      if (thumbnailsRef.current) {
        thumbnailsRef.current.scrollLeft = thumbnailsRef.current.scrollWidth;
      }
    }, 100);
  };

  const removeImage = (index: number) => {
    setCapturedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (capturedImages.length === 0) return;
    
    try {
      // Convert data URLs to File objects
      const files = await Promise.all(capturedImages.map(async (dataUrl, index) => {
        const res = await fetch(dataUrl);
        const blob = await res.blob();
        return new File([blob], `camera_capture_${Date.now()}_${index}.jpg`, { type: 'image/jpeg' });
      }));
      
      // Start the upload process but don't wait for it to complete
      onCapture(files); // Remove the await
    
      // Close the camera dialog immediately
      onClose();
    } catch (error) {
      console.error('Error processing captured images:', error);
    }
  };

  const handleCancel = () => {
    setCapturedImages([]);
  };

  // Check if device has multiple cameras
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
  
  useEffect(() => {
    // Only check on mobile devices
    if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
      navigator.mediaDevices.enumerateDevices()
        .then(devices => {
          const videoDevices = devices.filter(device => device.kind === 'videoinput');
          setHasMultipleCameras(videoDevices.length > 1);
        })
        .catch(err => {
          console.error('Error checking cameras:', err);
        });
    }
  }, []);

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      sx={{
        zIndex: 2005,
      }}
    >
      <AppBar position="static" color="primary">
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Take Photos
          </Typography>
          
          {/* Enhanced photo counter in header */}
          {capturedImages.length > 0 && (
            <Badge 
              badgeContent={capturedImages.length} 
              color="secondary"
              sx={{ 
                mr: 2,
                '& .MuiBadge-badge': {
                  fontSize: '1rem',
                  height: '1.5rem',
                  minWidth: '1.5rem'
                }
              }}
            >
              <PhotoCameraIcon />
            </Badge>
          )}
          
          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        bgcolor: '#000',
        position: 'relative',
        height: '100%'
      }}>
        {/* Camera preview */}
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          overflow: 'hidden',
          position: 'relative'
        }}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain'
            }}
          />
          
          {/* Flash effect overlay */}
          {flashEffect && (
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'white',
              opacity: 0.6,
              zIndex: 10,
              animation: 'flash 200ms ease-out',
              '@keyframes flash': {
                '0%': { opacity: 0.6 },
                '100%': { opacity: 0 }
              }
            }} />
          )}
          
          {/* Hidden canvas for capturing photos */}
          <canvas ref={canvasRef} style={{ display: 'none' }} />
          
          {/* Captured images count indicator - can remove this since we now have it in the header */}
          {capturedImages.length > 0 && (
            <Box sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              bgcolor: 'rgba(0, 0, 0, 0.6)',
              color: 'white',
              borderRadius: '50%',
              width: 36,
              height: 36,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              fontWeight: 'bold'
            }}>
              {capturedImages.length}
            </Box>
          )}
        </Box>
        
        {/* Image preview gallery */}
        {capturedImages.length > 0 && (
          <Box 
            ref={thumbnailsRef}
            sx={{ 
              display: 'flex',
              overflowX: 'auto',
              p: 1,
              bgcolor: 'rgba(0, 0, 0, 0.8)',
              height: '100px',
              '&::-webkit-scrollbar': {
                height: '4px'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(255,255,255,0.3)',
                borderRadius: '4px'
              }
            }}
          >
            {capturedImages.map((img, index) => (
              <Box 
                key={index} 
                sx={{ 
                  position: 'relative',
                  minWidth: '80px',
                  height: '80px',
                  m: 0.5,
                  borderRadius: 1,
                  overflow: 'hidden',
                  border: '2px solid white'
                }}
              >
                <img 
                  src={img} 
                  alt={`Captured ${index}`} 
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'cover',
                  }}
                />
                <IconButton
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    bgcolor: 'rgba(0,0,0,0.5)',
                    color: 'white',
                    p: 0.5,
                    '&:hover': {
                      bgcolor: 'rgba(255,0,0,0.7)'
                    }
                  }}
                  onClick={() => removeImage(index)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}

        {/* Control buttons */}
        <Box sx={{ 
          p: 2, 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'rgba(0, 0, 0, 0.8)'
        }}>
          {/* Left button - cancel or back */}
          <Box>
            {capturedImages.length > 0 ? (
              <Button 
                variant="text" 
                color="error" 
                onClick={handleCancel}
                sx={{ color: 'white' }}
              >
                Cancel
              </Button>
            ) : (
              /* Spacer */
              <Box sx={{ width: 64 }} />
            )}
          </Box>
          
          {/* Center - camera button */}
          <IconButton
            sx={{
              bgcolor: '#fff',  
              backgroundColor: 'rgb(255, 255, 255) !important',
              width: 64,
              height: 64,
              boxShadow: theme => theme.palette.mode === 'dark' 
                ? '0 0 16px rgba(255, 255, 255, 0.8)' // Stronger glow in dark mode
                : '0 0 8px rgba(255, 255, 255, 0.5)',
              border: '2px solid white',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                bgcolor: '#fff', 
                transform: 'scale(1.05)',
                boxShadow: theme => theme.palette.mode === 'dark'
                  ? '0 0 20px rgba(255, 255, 255, 0.9)'
                  : '0 0 12px rgba(255, 255, 255, 0.7)',
              },
              '&:disabled': {
                bgcolor: 'rgba(255, 255, 255, 0.3)',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                boxShadow: 'none',
                transform: 'none',
              }
            }}
            onClick={takePhoto}
            disabled={isCapturing || !stream || isUploading}
          >
            <PhotoCameraIcon sx={{ 
              fontSize: 36, 
              color: '#000' // Always black for contrast
            }} />
          </IconButton>
          
          {/* Right button - flip camera or upload */}
          <Box>
            {capturedImages.length > 0 ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleUpload}
                disabled={isUploading}
                startIcon={isUploading && <CircularProgress size={16} color="inherit" />}
              >
                {isUploading ? 'Uploading...' : `Upload (${capturedImages.length})`}
              </Button>
            ) : hasMultipleCameras ? (
              <IconButton 
                sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.3)'
                  }
                }}
                onClick={switchCamera}
              >
                <FlipCameraIosIcon />
              </IconButton>
            ) : (
              /* Spacer */
              <Box sx={{ width: 64 }} />
            )}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default CameraCapture;